import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ProjectPlanningStep } from '../../types';

interface EditPlanningStepModalProps {
  step: ProjectPlanningStep;
  onClose: () => void;
  onSubmit: (updateData: {
    stepName?: string;
    stepOrder?: number;
    description?: string;
    isRequired?: boolean;
    isCompleted?: boolean;
    stepData?: any;
    notes?: string;
  }) => void;
  existingSteps: ProjectPlanningStep[];
}

export const EditPlanningStepModal: React.FC<EditPlanningStepModalProps> = ({ 
  step, 
  onClose, 
  onSubmit, 
  existingSteps 
}) => {
  const [formData, setFormData] = useState({
    stepName: step.stepName,
    stepOrder: step.stepOrder,
    description: step.description || '',
    isRequired: step.isRequired,
    isCompleted: step.isCompleted,
    notes: step.notes || ''
  });

  const [jsonData, setJsonData] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    // Initialize JSON data from step
    setJsonData(JSON.stringify(step.stepData || {}, null, 2));
  }, [step]);

  const validateJson = (jsonString: string) => {
    if (!jsonString.trim()) {
      setJsonData('{}');
      setJsonError(null);
      return {};
    }

    try {
      const parsed = JSON.parse(jsonString);
      setJsonError(null);
      return parsed;
    } catch (error) {
      setJsonError('Ungültiges JSON-Format');
      return null;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.stepName.trim()) {
      return;
    }

    const parsedData = validateJson(jsonData);
    if (jsonError || parsedData === null) {
      return;
    }

    // Check if step order conflicts with existing steps (excluding current step)
    if (formData.stepOrder !== step.stepOrder) {
      const orderExists = existingSteps.some(existingStep => existingStep.stepOrder === formData.stepOrder);
      if (orderExists) {
        alert('Ein Schritt mit dieser Reihenfolge existiert bereits. Bitte wählen Sie eine andere Nummer.');
        return;
      }
    }

    // Only send changed fields
    const updateData: any = {};
    
    if (formData.stepName.trim() !== step.stepName) {
      updateData.stepName = formData.stepName.trim();
    }
    
    if (formData.stepOrder !== step.stepOrder) {
      updateData.stepOrder = formData.stepOrder;
    }
    
    const newDescription = formData.description.trim() || undefined;
    if (newDescription !== step.description) {
      updateData.description = newDescription;
    }
    
    if (formData.isRequired !== step.isRequired) {
      updateData.isRequired = formData.isRequired;
    }
    
    if (formData.isCompleted !== step.isCompleted) {
      updateData.isCompleted = formData.isCompleted;
    }
    
    const newNotes = formData.notes.trim() || undefined;
    if (newNotes !== step.notes) {
      updateData.notes = newNotes;
    }
    
    if (JSON.stringify(parsedData) !== JSON.stringify(step.stepData)) {
      updateData.stepData = parsedData;
    }

    onSubmit(updateData);
  };

  const usedOrders = existingSteps.map(s => s.stepOrder).sort((a, b) => a - b);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Planungsschritt bearbeiten</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 transition-colors"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Step Name */}
          <div>
            <label htmlFor="stepName" className="block text-sm font-medium text-gray-700 mb-2">
              Schritt-Name *
            </label>
            <input
              type="text"
              id="stepName"
              value={formData.stepName}
              onChange={(e) => setFormData(prev => ({ ...prev, stepName: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="z.B. Anforderungsanalyse, Marktforschung, Konzepterstellung..."
              required
            />
          </div>

          {/* Step Order */}
          <div>
            <label htmlFor="stepOrder" className="block text-sm font-medium text-gray-700 mb-2">
              Reihenfolge *
            </label>
            <div className="flex items-center space-x-4">
              <input
                type="number"
                id="stepOrder"
                min="1"
                value={formData.stepOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, stepOrder: parseInt(e.target.value) || 1 }))}
                className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <div className="text-sm text-gray-600">
                {usedOrders.length > 0 && (
                  <>
                    Andere Schritte: {usedOrders.join(', ')}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Beschreibung
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Detaillierte Beschreibung des Planungsschritts..."
            />
          </div>

          {/* Status Toggles */}
          <div className="space-y-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isRequired"
                checked={formData.isRequired}
                onChange={(e) => setFormData(prev => ({ ...prev, isRequired: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isRequired" className="ml-2 text-sm text-gray-900">
                Dieser Schritt ist erforderlich für den Projektabschluss
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isCompleted"
                checked={formData.isCompleted}
                onChange={(e) => setFormData(prev => ({ ...prev, isCompleted: e.target.checked }))}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor="isCompleted" className="ml-2 text-sm text-gray-900">
                Dieser Schritt ist abgeschlossen
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
              Notizen
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
              placeholder="Zusätzliche Notizen oder Hinweise..."
            />
          </div>

          {/* JSON Data */}
          <div>
            <label htmlFor="jsonData" className="block text-sm font-medium text-gray-700 mb-2">
              Zusätzliche Daten (JSON)
            </label>
            <textarea
              id="jsonData"
              value={jsonData}
              onChange={(e) => {
                setJsonData(e.target.value);
                validateJson(e.target.value);
              }}
              rows={6}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-blue-500 focus:border-blue-500 font-mono text-sm ${
                jsonError ? 'border-red-300 bg-red-50' : 'border-gray-300'
              }`}
              placeholder='{"duration_days": 7, "resources_needed": ["Team Lead", "Research Tools"]}'
            />
            {jsonError && (
              <p className="mt-1 text-sm text-red-600">{jsonError}</p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Strukturierte Daten für den Planungsschritt (z.B. Dauer, benötigte Ressourcen, etc.)
            </p>
          </div>

          {/* Metadata */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500">
              Erstellt: {new Date(step.createdAt).toLocaleString('de-DE')}
            </p>
            {step.completedAt && (
              <p className="text-xs text-gray-500">
                Abgeschlossen: {new Date(step.completedAt).toLocaleString('de-DE')}
              </p>
            )}
          </div>

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={!formData.stepName.trim() || !!jsonError}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Änderungen speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};